import type { DatesSetArg, EventClickArg, EventMountArg } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import type { DropDownResult } from '@myosh/odin-components';
import { isDropDownNoSelection } from '@myosh/odin-components';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { CalendarEvent, DataGridRecord } from '../../@types/calendar';
import type { FieldFilters } from '../../@types/common';
import type { Module } from '../../@types/modules';
import type { RecordStructure } from '../../@types/records';
import useFetchWorkflowSteps from '../../hooks/use-workflow-steps';
import { useAppSelector } from '../../redux/hooks';
import { useGetFormByIdQuery } from '../../redux/services/api';
import { useGetRecordsQuery } from '../../redux/services/record';
import { useGetViewColumnsQuery } from '../../redux/services/view';
import type { DynamicPageFilters } from '../../redux/slices/dynamic-page-slice';
import { selectedGlobalHierarchies, selectedMatchingOption } from '../../redux/slices/global-hierarchies';
import {
  buildCalendarFilters,
  extractDateFieldInfo,
  getCalendarHeight,
  getFieldValueById,
  isTodayVisible,
  makePopupDraggable,
  recordToCalendarEvent,
} from '../../utils/calendar-utils';
import { dateFormatBasedOnFilter } from '../../utils/data-transformation-utils';
import { buildGlobalHierarchyFiltersUrl, processFieldFilters } from '../../utils/filter-url-builder-utils';
import type { ActiveRecordRef } from '../active-record/active-record.component';
import { RecordTitle } from '../record/record-title.component';
import { constructRecordTitle } from '../record/util/record-component-util';
import { DataGridLoader } from './data-grid-loader.component';
import './fullcalendar-theme.css';

const CALENDAR_PAGE_SIZE = 1000;
const MAX_EVENTS_MONTH: number = 2;
const MAX_EVENTS_DAY_WEEK: number = 15;

interface OdinCalendarProps {
  selectedForm: DropDownResult;
  onDataLoadCompleted?: () => void;
  activeRecordReference?: React.MutableRefObject<ActiveRecordRef | null | undefined>;
  rowClickHandler?: (handleClick: (isFullScreen: boolean) => void) => void;
  module?: Module;
  isAllForms?: boolean;
  selectedFilters?: DynamicPageFilters;
}

export const OdinCalendar = ({
  selectedForm,
  onDataLoadCompleted,
  activeRecordReference,
  rowClickHandler,
  module,
  isAllForms = false,
  selectedFilters,
}: OdinCalendarProps) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentView, setCurrentView] = useState('dayGridMonth');
  const [dateRange, setDateRange] = useState<{ start: Date; end: Date } | null>(null);
  const calendarRef = useRef<FullCalendar>(null);
  const [calendarHeight, setCalendarHeight] = useState(() => getCalendarHeight());
  const globalFilters = useAppSelector(selectedGlobalHierarchies);
  const globalFiltersMatchingOption = useAppSelector(selectedMatchingOption);

  // Get view configuration to apply view-specific field filters
  const { data: viewColumnsData, isFetching: isViewColumnsFetching } = useGetViewColumnsQuery(
    {
      configId: selectedFilters?.viewConfig?.value as number,
    },
    {
      skip:
        !selectedFilters?.viewConfig?.value ||
        (selectedFilters.viewConfig.value as number) <= 0 ||
        selectedFilters.viewConfig.value === undefined,
    }
  );

  // Build calendar-specific filters using the extracted utility function
  const calendarFilters = useMemo(() => {
    return buildCalendarFilters(selectedFilters, viewColumnsData);
  }, [selectedFilters, viewColumnsData]);

  const { data: rawFormSettings } = useGetFormByIdQuery(selectedForm?.value as number, {
    skip: isDropDownNoSelection(selectedForm) || (selectedForm?.value as number) <= 0,
  });

  const dateFieldInfo = useMemo(() => {
    return extractDateFieldInfo(rawFormSettings);
  }, [rawFormSettings]);

  const { workflowSteps } = useFetchWorkflowSteps(selectedForm?.value as number);

  const globalHierarchyFiltersResult = buildGlobalHierarchyFiltersUrl(globalFilters, globalFiltersMatchingOption);

  // Build the query parameters object conditionally
  const queryParams: {
    keyTypeId: boolean;
    formId: number;
    filters: FieldFilters;
    page: number;
    pageSize: number;
    globalHierarchyFilters?: string;
  } = {
    keyTypeId: true,
    formId: selectedForm?.value as number,
    filters: processFieldFilters(calendarFilters, dateFormatBasedOnFilter), // Process filters like single-form-grid
    page: 1,
    pageSize: CALENDAR_PAGE_SIZE,
  };

  // Only add globalHierarchyFilters if it's not undefined and not empty
  if (globalHierarchyFiltersResult !== undefined && globalHierarchyFiltersResult !== '') {
    queryParams.globalHierarchyFilters = globalHierarchyFiltersResult;
  }

  const {
    data: recordsData,
    isFetching: isRecordsFetching,
    error: recordsError,
  } = useGetRecordsQuery(queryParams, {
    skip:
      selectedForm === undefined ||
      (selectedForm?.value as number) <= 0 ||
      isDropDownNoSelection(selectedForm) ||
      isViewColumnsFetching ||
      !selectedFilters?.viewConfig ||
      !selectedFilters?.records ||
      !selectedFilters?.lastEdited, // Wait for all required filters to be available
  });
  // Filter records that have valid start dates
  const validRecords = useMemo(() => {
    if (!recordsData || recordsData.length === 0) return [];

    return recordsData.filter((record: RecordStructure) => {
      const item = record.flat;

      if (dateFieldInfo.startFieldId) {
        // Using configured start date field - MANDATORY
        const startDateValue = getFieldValueById(item, dateFieldInfo.startFieldId);
        return !!(startDateValue && startDateValue.trim() !== '');
      } else {
        // Using fallback calendarViewDate field - MANDATORY
        const calendarDate = item.calendarViewDate as string;
        return !!(calendarDate && calendarDate.trim() !== '');
      }
    });
  }, [recordsData, dateFieldInfo.startFieldId]);

  // Convert data grid records to calendar events
  const calendarEvents: CalendarEvent[] = useMemo(() => {
    if (validRecords.length === 0) return [];
    const formDisplayName = selectedForm?.text || 'Record';

    return validRecords.map((record: RecordStructure, index: number) => {
      return recordToCalendarEvent(record, index, dateFieldInfo, workflowSteps, formDisplayName);
    });
  }, [validRecords, selectedForm?.text, dateFieldInfo, workflowSteps]);

  useEffect(() => {
    if (!isRecordsFetching && !isViewColumnsFetching) {
      onDataLoadCompleted?.();
    }
  }, [isRecordsFetching, isViewColumnsFetching, onDataLoadCompleted]);

  useEffect(() => {
    const updateHeight = () => setCalendarHeight(getCalendarHeight());
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  const handleMoreLinkClick = () => {
    makePopupDraggable();
    return 'popover';
  };

  const showTodayButton = !isTodayVisible(currentDate, currentView);

  // Dynamic header toolbar based on today button visibility
  const headerToolbar = useMemo(() => {
    const leftButtons = showTodayButton ? 'prev,next today' : 'prev,next';

    return {
      left: leftButtons,
      center: 'title',
      right: 'dayGridMonth,dayGridWeek,dayGridDay',
    };
  }, [showTodayButton]);

  const handleEventClick = (clickInfo: EventClickArg) => {
    const recordData = clickInfo.event.extendedProps?.recordData as DataGridRecord;

    if (!recordData || !activeRecordReference || !rowClickHandler || !module) {
      return;
    }

    const handleClick = (isFullScreen: boolean) => {
      const recordId = recordData.id;
      const docNo = String(recordData.docNo || '');
      const status = String(recordData.status || '');
      const formName = isAllForms ? String(recordData.moduleForm || '') : String(recordData.formName || '');
      const date = recordData.creationDate ? new Date(recordData.creationDate as string) : undefined;
      const title = constructRecordTitle(formName, docNo, status);
      const details = <RecordTitle title={title} date={date} />;

      activeRecordReference.current?.addActiveRecord(String(recordId), module.icon, details, isFullScreen, {
        title,
        recordId,
        viewId: module.viewId,
        moduleIcon: module.icon,
        formId: recordData.formId,
        formName: formName,
        docNo,
        status,
        vikingLinkUrl: recordData.vikingLinkUrl,
        displayText: recordData.displayText,
      });
    };

    rowClickHandler(handleClick);
  };

  const handleEventDidMount = (info: EventMountArg) => {
    info.el.setAttribute('title', info.event.title);
    info.el.style.cursor = 'pointer';
  };

  const canRenderCalendar = useMemo(() => {
    return selectedForm !== undefined && !isDropDownNoSelection(selectedForm);
  }, [selectedForm]);

  const showCalendarLoader = useMemo(() => {
    const hasValidForm = selectedForm !== undefined && !isDropDownNoSelection(selectedForm);

    if (!hasValidForm) {
      return false; // Don't show loader if no valid form
    }

    // Show loader if no date range yet (calendar hasn't initialized)
    if (dateRange === null) {
      return true;
    }

    // Show loader if actively fetching data
    return isViewColumnsFetching || isRecordsFetching;
  }, [selectedForm, dateRange, isRecordsFetching, isViewColumnsFetching]);

  // The calendar should show when we have a valid form, regardless of loading state
  const showCalendar = useMemo(() => {
    return canRenderCalendar && !recordsError;
  }, [canRenderCalendar, recordsError]);

  const handleDatesSet = (dateInfo: DatesSetArg) => {
    // For month view, calculate the middle of the date range to get the actual month
    const startDate = new Date(dateInfo.start);
    const endDate = new Date(dateInfo.end);
    const middleDate = new Date((startDate.getTime() + endDate.getTime()) / 2);
    setCurrentDate(middleDate);

    // Update date range for API filtering
    setDateRange({
      start: startDate,
      end: endDate,
    });

    setCurrentView(dateInfo.view.type);
  };

  return (
    <div className="relative h-full">
      <div className="h-full pr-2">
        {recordsError && (
          <div className="mb-2 rounded border border-error px-4 py-3 text-error">
            Error loading calendar data. Please try again.
          </div>
        )}

        {showCalendarLoader && !recordsError && (
          <div className="flex h-full items-center justify-center">
            <DataGridLoader isCalendarView />
          </div>
        )}
        {showCalendar && (
          <div className="w-full">
            <FullCalendar
              ref={calendarRef}
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
              headerToolbar={headerToolbar} // Use dynamic header
              initialView="dayGridMonth"
              editable={false}
              selectable={true}
              selectMirror={true}
              weekends={true}
              events={calendarEvents}
              height={calendarHeight}
              contentHeight="auto"
              expandRows={true}
              eventClick={handleEventClick}
              eventDidMount={handleEventDidMount}
              moreLinkClick={handleMoreLinkClick}
              eventDisplay="block"
              allDaySlot={true}
              displayEventTime={false}
              nowIndicator={true}
              eventOverlap={true}
              slotEventOverlap={false}
              moreLinkClassNames={['fc-more-link']}
              eventOrder="start,-duration,title"
              fixedWeekCount={false}
              showNonCurrentDates={true}
              datesSet={handleDatesSet}
              views={{
                dayGridMonth: {
                  dayMaxEvents: MAX_EVENTS_MONTH,
                },
                timeGridWeek: {
                  allDaySlot: true,
                  slotLabelFormat: [],
                  slotMinTime: '00:00:00',
                  slotMaxTime: '00:00:00',
                  slotDuration: '24:00:00',
                  dayMaxEvents: MAX_EVENTS_DAY_WEEK,
                },
                timeGridDay: {
                  allDaySlot: true,
                  slotLabelFormat: [],
                  slotMinTime: '00:00:00',
                  slotMaxTime: '00:00:00',
                  slotDuration: '24:00:00',
                  dayMaxEvents: MAX_EVENTS_DAY_WEEK,
                },
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default OdinCalendar;
